import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import {
  fetchFileRequests, createFileRequest, closeFileRequest, reopenFileRequest, deleteFileRequest,
  fetchFolders, fetchMySpace,
} from '../services/api';
import type { FileRequest } from '../types';
import Button from '../components/Button';
import Chip from '../components/Chip';
import Modal from '../components/Modal';
import Icon from '../components/Icon';
import { useAppStore } from '../store/useAppStore';

const shareUrl = (token: string) => `${window.location.origin}/r/${token}`;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

// A request stops accepting uploads once closed or past its deadline (the public
// endpoint enforces both) — reflect that here rather than trusting status alone.
function requestStatus(r: FileRequest): { label: string; tone: 'success' | 'neutral' | 'warn'; open: boolean } {
  if (r.status === 'closed') return { label: 'Closed', tone: 'neutral', open: false };
  if (r.deadline && new Date(r.deadline).getTime() < Date.now()) {
    return { label: 'Deadline passed', tone: 'warn', open: false };
  }
  return { label: 'Open', tone: 'success', open: true };
}

export default function Requests() {
  const { toast } = useAppStore();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [deadline, setDeadline] = useState('');
  const [targetFolder, setTargetFolder] = useState('');
  const [created, setCreated] = useState<FileRequest | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<FileRequest | null>(null);

  // File requests deposit into a space; `orgId` is a tenant id, not a space id.
  const { data: mySpace } = useQuery({
    queryKey: ['spaces', 'me'],
    queryFn: fetchMySpace,
    enabled: showCreate,
  });
  const spaceId = mySpace?.id;

  const { data: folders = [] } = useQuery({
    queryKey: qk.folders(spaceId ?? ''),
    queryFn: () => fetchFolders(spaceId!),
    enabled: showCreate && !!spaceId,
  });

  const { data: requests = [], isLoading } = useQuery({
    queryKey: qk.fileRequests(),
    queryFn: fetchFileRequests,
  });

  const createMutation = useMutation({
    mutationFn: () => createFileRequest(title.trim(), spaceId!, targetFolder || undefined, deadline || undefined),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: qk.fileRequests() });
      setCreated(r);
      setShowCreate(false);
      setTitle(''); setDeadline(''); setTargetFolder('');
      toast('File request created');
    },
    onError: (e: Error) => toast(e.message || 'Failed to create request', 'danger'),
  });

  const closeMutation = useMutation({
    mutationFn: (id: string) => closeFileRequest(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.fileRequests() }); toast('Request closed'); },
    onError: (e: Error) => toast(e.message || 'Failed to close request', 'danger'),
  });

  const reopenMutation = useMutation({
    mutationFn: (id: string) => reopenFileRequest(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.fileRequests() }); toast('Request reopened'); },
    onError: (e: Error) => toast(e.message || 'Failed to reopen request', 'danger'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFileRequest(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.fileRequests() }); toast('Request deleted'); },
    onError: (e: Error) => toast(e.message || 'Failed to delete request', 'danger'),
  });

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--line)', background: 'var(--card)' }}>
        <div>
          <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 700 }}>File requests</h2>
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', marginTop: 2 }}>Collect files from anyone into a folder you choose.</div>
        </div>
        <Button kind="accent" icon="plus" onClick={() => setShowCreate(true)}>New request</Button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[0, 1, 2].map(i => <div key={i} className="t-skeleton" style={{ height: 80, borderRadius: 10 }} />)}
          </div>
        ) : requests.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 12, color: 'var(--ink-3)' }}>
            <Icon name="inbox" size={40} color="var(--ink-4)" />
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink-2)' }}>No file requests</div>
            <div style={{ fontSize: 13 }}>Create a request link for others to upload files to you.</div>
            <Button kind="accent" icon="plus" onClick={() => setShowCreate(true)}>New request</Button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {requests.map(r => (
              <RequestCard
                key={r.id}
                request={r}
                onClose={() => closeMutation.mutate(r.id)}
                onReopen={() => reopenMutation.mutate(r.id)}
                onDelete={() => setConfirmDelete(r)}
                onCopy={() => { navigator.clipboard.writeText(shareUrl(r.token)); toast('Link copied'); }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Created success */}
      {created && (
        <Modal open onClose={() => setCreated(null)} title="Request created" size="sm">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="t-alert t-alert--success"><Icon name="check" size={14} />Share this link to collect files.</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                className="t-input t-input--sm"
                readOnly
                value={shareUrl(created.token)}
                style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 11 }}
              />
              <Button kind="outline" size="sm" icon="copy" onClick={() => { navigator.clipboard.writeText(shareUrl(created.token)); toast('Link copied'); }}>Copy</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete confirmation */}
      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Delete file request"
        size="sm"
        footer={
          <>
            <Button kind="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              kind="danger"
              onClick={() => {
                if (confirmDelete) deleteMutation.mutate(confirmDelete.id);
                setConfirmDelete(null);
              }}
            >
              Delete
            </Button>
          </>
        }
      >
        {confirmDelete && (
          <span>
            Delete <strong>{confirmDelete.title}</strong>? The link stops working immediately. Files already
            received stay in your drive.
          </span>
        )}
      </Modal>

      {/* Create modal */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create file request"
        footer={
          <>
            <Button kind="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              kind="accent"
              icon="inbox"
              loading={createMutation.isPending}
              disabled={!title.trim() || !spaceId}
              onClick={() => createMutation.mutate()}
            >
              Create request
            </Button>
          </>
        }
      >
        <div className="t-stack">
          <div className="t-alert t-alert--info" style={{ alignItems: 'flex-start', gap: 10 }}>
            <Icon name="inbox" size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>Anyone with the link can upload files to you — even without an SK account. Uploads land in the folder you choose; they can't see anything else.</span>
          </div>
          <div className="t-field">
            <label>What are you requesting?</label>
            <input className="t-input" placeholder="e.g. Candidate portfolios" value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div className="t-field">
            <label>Upload files to</label>
            <select className="t-input" value={targetFolder} onChange={e => setTargetFolder(e.target.value)}>
              <option value="">My Drive</option>
              {folders.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          <div className="t-field">
            <label>Deadline (optional)</label>
            <input
              className="t-input"
              type="date"
              value={deadline}
              min={new Date().toISOString().slice(0, 10)}
              onChange={e => setDeadline(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

function RequestCard({ request: r, onClose, onReopen, onDelete, onCopy }: {
  request: FileRequest;
  onClose: () => void;
  onReopen: () => void;
  onDelete: () => void;
  onCopy: () => void;
}) {
  const status = requestStatus(r);
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      {/* Icon */}
      <div style={{ width: 38, height: 38, borderRadius: 8, background: 'var(--accent-soft)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        <Icon name="inbox" size={18} color="var(--accent)" />
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <span style={{ fontWeight: 600, fontSize: 'var(--fs-md)' }}>{r.title}</span>
          <Chip tone={status.tone} dot>{status.label}</Chip>
        </div>
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icon name="folder" size={12} />
            {r.folder_name ?? 'My Drive'}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icon name="download" size={12} />
            {r.received_count} received
          </span>
          {r.deadline && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icon name="calendar" size={12} />
              Due {formatDate(r.deadline)}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
        {r.status === 'open'
          ? <Button kind="ghost" size="sm" icon="lock" iconOnly onClick={onClose} title="Close request" />
          : <Button kind="outline" size="sm" onClick={onReopen}>Reopen</Button>}
        <Button kind="ghost" size="sm" icon="trash" iconOnly onClick={onDelete} title="Delete request" />
        {status.open && (
          <Button kind="outline" size="sm" icon="copy" onClick={onCopy}>Copy</Button>
        )}
      </div>
    </div>
  );
}
